import type { StringKey } from '@ipfabric/utils/types/StringKey';
import { TextInput } from 'components/TextInput/TextInput';
import type { FormikProps } from 'formik';
import type React from 'react';
import { type RefObject, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

type InputProps = {
  value: string;
  onClick: () => void;
  placeholderText: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

interface Props<TFormikValues> {
  formik: FormikProps<TFormikValues>;
  name: StringKey<TFormikValues> | NestedFormikName;
  placeholderText: string;
  dateFormat: string;
  disabledBeforeToday?: boolean;
}

export const FormikDatePickerField = <TFormikValues,>({
  formik,
  ...props
}: Props<TFormikValues>) => {
  const field = formik.getFieldProps(props.name);

  const DatePickerCustomInput = forwardRef((inputProps, ref) => {
    // Workaround for the issue with the DatePicker component not being able to handle the props and ref properly
    // https://github.com/Hacker0x01/react-datepicker/issues/4345
    const shadowProps = {
      ...inputProps,
      placeholderText: props.placeholderText,
    } as InputProps;
    const shadowRef = ref as RefObject<HTMLInputElement>;

    return (
      <TextInput
        // override react-datepicker's style
        className='[&_input]:!border-none'
        type='text'
        value={shadowProps.value}
        onClick={shadowProps.onClick}
        inputRef={shadowRef}
        placeholder={shadowProps.placeholderText}
        onChange={shadowProps.onChange}
      />
    );
  });

  return (
    <DatePicker
      {...(props.disabledBeforeToday && {
        minDate: new Date(),
      })}
      {...field}
      {...props}
      selected={field.value}
      onChange={(val) => {
        formik.setFieldValue(field.name, val);
      }}
      customInput={<DatePickerCustomInput />}
    />
  );
};
